import { useCallback, useEffect, useState } from "react";
import { STATUS_COLOR, STATUS_LABEL } from "./data";
import { useNavigate } from "react-router-dom";
import {
  exportSaleReportDailyGrid,
  getListSaleReport,
  updateMultipleSaleReport,
} from "../../../services";
import ExportButton from "../../ExportButton";
import { SalesReport } from "../../../types/sale-reports.type";
import { formatDate } from "../../../utils/date";
import { formatNumber, numberWithCommas } from "../../../utils";
import { useDebounce } from "../../../utils/hooks/useDebounce";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import dayjs, { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import ReportFilter from "./Filter";
import ReportPagination from "./Pagination";
import NoRunDaysSection from "./NoRunDaysSection";
import Skeleton from "@mui/material/Skeleton";
import Checkbox from "@mui/material/Checkbox";
import ConfirmModal from "../../ConfirmModal";

interface ReportTableProps {
  triggerFetch?: number;
  showTitle?: boolean;
  showFilter?: boolean;
  showPagination?: boolean;
  fixedLimit?: number;
  defaultDate?: Dayjs | null;
  /** List สายไม่วิ่ง days for the same filters, above the report table. */
  showNoRunDays?: boolean;
}

const renderSkeletonRows = (columnCount: number) => {
  return Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: columnCount }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
};

const LIMIT = 10;

export default function ReportTable({
  triggerFetch,
  showTitle = false,
  showFilter = true,
  showPagination = true,
  fixedLimit,
  defaultDate,
  showNoRunDays = false,
}: ReportTableProps) {
  const role = useUserRole();
  const { isHeadOfficeAccounting } = role;

  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(fixedLimit || LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [reports, setReports] = useState<SalesReport[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    defaultDate || dayjs(),
  );
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [selectedRoute, setSelectedRoute] = useState<string>("all");
  const [selectedBillId, setSelectedBillId] = useState<string>("");
  const [customerName, setCustomerName] = useState<string>("");
  const [customerCode, setCustomerCode] = useState<string>("");
  const [taxId, setTaxId] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openConfirmUpdateStatus, setOpenConfirmUpdateStatus] =
    useState<boolean>(false);

  const customerNameSearch = useDebounce(customerName, 500);
  const customerCodeSearch = useDebounce(customerCode, 500);
  const taxIdSearch = useDebounce(taxId, 500);
  const billIdSearch = useDebounce(selectedBillId, 500);

  const getSaleReports = useCallback(
    async (date?: string) => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await getListSaleReport(
          page,
          limit,
          date,
          selectedBranch,
          billIdSearch,
          customerNameSearch,
          customerCodeSearch,
          taxIdSearch,
          selectedRoute && selectedRoute !== "all"
            ? [selectedRoute]
            : undefined,
        );

        setReports(data.rows);
        setTotalCount(data.count);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      limit,
      selectedBranch,
      selectedRoute,
      billIdSearch,
      customerNameSearch,
      customerCodeSearch,
      taxIdSearch,
    ],
  );

  useEffect(() => {
    getSaleReports(selectedDate?.format("YYYY-MM-DD"));
  }, [triggerFetch, selectedDate, getSaleReports]);

  useEffect(() => {
    if (defaultDate) {
      setSelectedDate(defaultDate);
    }
  }, [defaultDate]);

  const isSelectableStatus = (status: string) =>
    isHeadOfficeAccounting && status === "branch account approved";

  const handleSelectReport = (id: string, status: string) => {
    if (!isSelectableStatus(status)) return;

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const approvedRows = reports.filter((report) =>
    isSelectableStatus(report.status),
  );

  const isHasSelectableStatus = reports.some((report) =>
    isSelectableStatus(report.status),
  );

  const handleSelectReportAll = (checked: boolean) => {
    if (checked) {
      const approvedIds = approvedRows.map((r) => r.uuid);
      setSelectedIds(approvedIds);
    } else {
      setSelectedIds([]);
    }
  };

  const onUpdateSaleReport = async () => {
    // Guarded the same way as the report-detail footer: each id in the batch is
    // a sales_report re-version, so a second click while the first request is in
    // flight approves every selected report twice. Tracked separately from
    // `loading` (the table's own fetch flag) so the guard cannot be tripped by an
    // unrelated refetch.
    if (submitting) return;

    setSubmitting(true);
    setLoading(true);
    try {
      await updateMultipleSaleReport(selectedIds);
      setOpenConfirmUpdateStatus(false);
      setSelectedIds([]);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Paper>
        {showFilter && (
          <ReportFilter
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            selectedRoute={selectedRoute}
            setSelectedRoute={setSelectedRoute}
            selectedBillId={selectedBillId}
            setSelectedBillId={setSelectedBillId}
            taxId={taxId}
            setTaxId={setTaxId}
            customerName={customerName}
            setCustomerName={setCustomerName}
            customerCode={customerCode}
            setCustomerCode={setCustomerCode}
          />
        )}
        {showNoRunDays && (
          <NoRunDaysSection
            selectedDate={selectedDate}
            selectedBranch={selectedBranch}
            selectedRoute={selectedRoute}
            showBranch={Boolean(isHeadOfficeAccounting)}
            triggerFetch={triggerFetch}
          />
        )}
        {showTitle && (
          <Typography variant="body1" fontWeight={600} mb={2}>
            รายงานการขาย (Sale Daily Report)
          </Typography>
        )}
        {!showTitle && selectedIds.length !== 0 && (
          <Box display="flex" justifyContent="flex-end" gap={1} mb="16px">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenConfirmUpdateStatus(true)}
            >
              ยื่นรอส่ง E-tax ทั้งหมด
            </Button>
          </Box>
        )}
        <TableContainer>
          {/* min-width:max-content + nowrap cells keep every column at its own content
              width and never compress — the container scrolls horizontally. */}
          <Table sx={{ minWidth: "max-content" }}>
            <TableHead
              sx={{
                bgcolor: (theme) => theme.palette.background.default,
              }}
            >
              <TableRow sx={{ whiteSpace: "pre" }}>
                {!showTitle &&
                  reports.length !== 0 &&
                  isHasSelectableStatus && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          approvedRows.length > 0 &&
                          approvedRows.every((report) =>
                            selectedIds.includes(report.uuid),
                          )
                        }
                        indeterminate={
                          selectedIds.length > 0 &&
                          selectedIds.length < approvedRows.length
                        }
                        onChange={(e) =>
                          handleSelectReportAll(e.target.checked)
                        }
                      />
                    </TableCell>
                  )}
                {!defaultDate && (
                  <TableCell
                    sx={{
                      borderRight: (theme) =>
                        `1px solid ${theme.palette.draft.main}`,
                    }}
                  >
                    วันที่
                  </TableCell>
                )}
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                  }}
                >
                  สายส่ง
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                    width: 70,
                    whiteSpace: "normal",
                  }}
                >
                  % ของเสีย
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                    width: 80,
                    whiteSpace: "normal",
                  }}
                >
                  จำนวน
                  <br />
                  ร้านค้า
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                    width: 100,
                    whiteSpace: "normal",
                  }}
                >
                  จำนวน
                  <br />
                  ใบแจ้งหนี้
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                    width: 100,
                    whiteSpace: "normal",
                  }}
                >
                  จำนวนบิล
                  <br />
                  ขายสด
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                    width: 80,
                    whiteSpace: "normal",
                  }}
                >
                  จำนวนบิล
                  <br />
                  ยกเลิก
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                  }}
                >
                  เป้ายอดขาย
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                  }}
                >
                  ยอดขายจริง
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                  }}
                >
                  ยอดเงินเชื่อ
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                  }}
                >
                  ยอดเงินสด
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                  }}
                >
                  สถานะ
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ width: 130, whiteSpace: "pre" }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                renderSkeletonRows(
                  (defaultDate ? 12 : 13) +
                    (!showTitle && isHasSelectableStatus ? 1 : 0),
                )}
              {!loading && reports.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={defaultDate ? 12 : 13}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่พบข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                reports.length > 0 &&
                reports.map((report, index) => (
                  <TableRow
                    key={report.uuid}
                    sx={{
                      bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      "& td, & th": {
                        padding: "0px 8px",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {!showTitle && isHasSelectableStatus && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(report.uuid)}
                          onChange={() =>
                            handleSelectReport(report.uuid, report.status)
                          }
                        />
                      </TableCell>
                    )}
                    {!defaultDate && (
                      <TableCell>{formatDate(report.created_at)}</TableCell>
                    )}
                    <TableCell>
                      <IconButton
                        sx={{
                          bgcolor: (theme) => theme.palette.primary.main + "1A",
                          cursor: "default",
                          "&:hover, &:focus": {
                            bgcolor: (theme) =>
                              theme.palette.primary.main + "1A",
                          },
                          mr: 1,
                        }}
                      >
                        <AirportShuttleOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: (theme) => theme.palette.primary.main,
                          }}
                        />
                      </IconButton>
                      {report.route}
                    </TableCell>
                    <TableCell
                      sx={{
                        color:
                          report.waste_percent_total > 50
                            ? "#E16C6C"
                            : "inherit",
                      }}
                    >
                      {report.waste_percent_total}%
                    </TableCell>
                    <TableCell>
                      {numberWithCommas(report.customer_count)}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {numberWithCommas(report.invoice_count)}
                        {report.invoice_cancel_count != null && (
                          <Typography
                            variant="caption"
                            sx={{ color: "error.main" }}
                          >
                            ({numberWithCommas(report.invoice_cancel_count)})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {report.cashsale_count == null
                          ? "-"
                          : numberWithCommas(report.cashsale_count)}
                        {report.cashsale_cancel_count != null && (
                          <Typography
                            variant="caption"
                            sx={{ color: "error.main" }}
                          >
                            ({numberWithCommas(report.cashsale_cancel_count)})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {numberWithCommas(report.bill_cancel_count)}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {formatNumber(report.sales_target)}
                        <Chip
                          label={`${
                            report.actual_sales > report.sales_target ? "+" : ""
                          }${report.compare_sales_percent}%`}
                          variant="filled"
                          sx={{
                            height: 28,
                            fontWeight: 600,
                            color: (theme) =>
                              report.actual_sales > report.sales_target
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                            bgcolor: (theme) =>
                              report.actual_sales > report.sales_target
                                ? theme.palette.success.main + "1A"
                                : theme.palette.error.main + "1A",
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{formatNumber(report.actual_sales)}</TableCell>
                    <TableCell>
                      {report.credit_total_value == null
                        ? "-"
                        : formatNumber(report.credit_total_value)}
                    </TableCell>
                    <TableCell>
                      {report.cash_total_value == null
                        ? "-"
                        : formatNumber(report.cash_total_value)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            flexShrink: 0,
                            borderRadius: "50%",
                            // @ts-ignore
                            bgcolor: STATUS_COLOR[report.status],
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {/* @ts-ignore */}
                          {STATUS_LABEL[report.status]}
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* One action cell, icons only: the three labels spelled out
                        cost ~300px of a table that already scrolls sideways. */}
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Box
                        display="flex"
                        gap={0.5}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Tooltip title="ดูรายงาน">
                          <IconButton
                            size="small"
                            color="info"
                            aria-label="ดูรายงาน"
                            onClick={() =>
                              navigate(`/report/sale/details/${report.uuid}`)
                            }
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="ดูรายละเอียดเพิ่มเติม">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="ดูรายละเอียดเพิ่มเติม"
                            onClick={() =>
                              navigate(
                                `/report/sale/more-details/${report.uuid}`,
                              )
                            }
                          >
                            <GridOnOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* The same workbook the รายละเอียดเพิ่มเติม page
                            downloads — straight from the row, so nobody has to
                            open the report to get its file. */}
                        <ExportButton
                          iconOnly
                          label="ดาวน์โหลด Excel (รายละเอียดเพิ่มเติม)"
                          request={() => exportSaleReportDailyGrid(report.uuid)}
                          fallbackFilename={`sale-daily-report-detail_${
                            report.route
                          }_${dayjs(report.created_at).format("YYYYMMDD")}.xlsx`}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination && (
          <ReportPagination
            totalCount={totalCount}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
          />
        )}
      </Paper>
      <ConfirmModal
        title="ยืนยันอัพเดทข้อมูลรายงาน"
        description="คุณแน่ใจหรือไม่ว่าต้องการอัพเดทข้อมูลรายงานทั้งหมดนี้?"
        open={openConfirmUpdateStatus}
        loading={submitting}
        onClose={() => {
          if (!submitting) setOpenConfirmUpdateStatus(false);
        }}
        onConfirm={onUpdateSaleReport}
      />
    </>
  );
}
